import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, Zap, Clock, RefreshCw, Loader2, ClockAlert } from 'lucide-react';

/**
 * Live Ops Health Widget — three single-glance counters for dispatch:
 *  1. At-Risk (talent missed day-of confirmation, still unconfirmed)
 *  2. Urgent Fill Active (open replacement searches in last 24h)
 *  3. Within 24h Short-Staffed (shifts starting <24h away with confirmed < needed)
 */
const LiveOpsHealthWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get('/admin/live-ops/health');
      setData(res.data);
    } catch (_) {
      // silent — keep stale data
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh every 60s in the background so dispatch sees live counters.
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const tiles = [
    {
      key: 'at_risk',
      label: 'At-Risk',
      sublabel: 'missed day-confirm',
      value: data?.at_risk_count ?? 0,
      icon: AlertTriangle,
      color: 'text-red-700',
      bg: 'bg-red-50',
      border: 'border-red-200',
      onClick: () => navigate('/admin/calendar'),
      testid: 'liveops-tile-at-risk',
    },
    {
      key: 'urgent_fill',
      label: 'Urgent Fill Active',
      sublabel: data?.urgent_fill_rate?.enabled
        ? `${data.urgent_fill_rate.mode === 'percent' ? data.urgent_fill_rate.value + '%' : '$' + Number(data.urgent_fill_rate.value).toFixed(2)} uplift on`
        : 'uplift OFF',
      value: data?.urgent_fill_open_count ?? 0,
      icon: Zap,
      color: 'text-orange-700',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      onClick: () => navigate('/admin/calendar'),
      testid: 'liveops-tile-urgent-fill',
    },
    {
      key: 'within_24h',
      label: 'Within 24h Short-Staffed',
      sublabel: 'fewer talent than needed',
      value: data?.shifts_within_24h_short ?? 0,
      icon: Clock,
      color: 'text-amber-800',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      onClick: () => navigate('/admin/calendar'),
      testid: 'liveops-tile-within-24h',
    },
    {
      key: 'clockout_hygiene',
      label: 'Clock-Out Hygiene',
      sublabel: 'duration > shift × 1.5',
      value: data?.clockout_hygiene_count ?? 0,
      icon: ClockAlert,
      color: 'text-rose-700',
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      onClick: () => navigate('/admin/clockout-hygiene'),
      testid: 'liveops-tile-clockout-hygiene',
    },
  ];

  return (
    <Card className="p-4 border-2 border-cyan-200" data-testid="live-ops-health-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Live Ops Health</div>
          <div className="text-[11px] text-gray-500">
            single-glance dispatch scoreboard · auto-refresh 60s
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); load(true); }}
          disabled={loading}
          data-testid="liveops-refresh-btn"
          className="h-7 px-2"
          title="Refresh now"
        >
          <span className="pointer-events-none inline-flex">
            {refreshing
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5" />}
          </span>
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-6 text-gray-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {tiles.map((t) => {
            const Icon = t.icon;
            const isHot = t.value > 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={t.onClick}
                data-testid={t.testid}
                className={`text-left rounded-lg p-3 border transition hover:shadow-sm ${t.border} ${t.bg} ${isHot ? 'ring-1 ring-offset-0 ring-current ' + t.color : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`text-2xl font-bold ${t.color}`} data-testid={`${t.testid}-value`}>
                      {t.value}
                    </div>
                    <div className={`text-xs font-medium ${t.color}`}>{t.label}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{t.sublabel}</div>
                  </div>
                  <Icon className={`w-4 h-4 ${t.color} mt-1 shrink-0`} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default LiveOpsHealthWidget;
